import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as trainersApi from '../../api/trainers'
import * as linksApi from '../../api/links'
import { useUIStore } from '../../stores/uiStore'
import Button from '../common/Button'
import { XMarkIcon, UserPlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

/**
 * LinkTrainerModal Component
 * Modal for linking trainers to a contract
 */
export default function LinkTrainerModal({ contract, isOpen, onClose, onSuccess }) {
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTrainerId, setSelectedTrainerId] = useState(null)
  const [sendEmail, setSendEmail] = useState(false)

  // Fetch all trainers
  const { data: trainersData, isLoading } = useQuery({
    queryKey: ['trainers', { search: searchQuery }],
    queryFn: () => trainersApi.getTrainers({ search: searchQuery, limit: 50 }),
    enabled: isOpen,
  })

  // Link trainer mutation
  const linkMutation = useMutation({
    mutationFn: ({ trainerId, contractId, sendEmail }) => 
      linksApi.linkTrainerContract(trainerId, contractId, sendEmail),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['contract', contract.id])
      queryClient.invalidateQueries(['trainers'])
      showToast(
        sendEmail 
          ? 'Trainer linked and email sent successfully' 
          : 'Trainer linked successfully',
        'success'
      )
      if (onSuccess) onSuccess(data)
      handleClose()
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to link trainer', 'error')
    },
  })

  const handleClose = () => {
    setSearchQuery('')
    setSelectedTrainerId(null)
    setSendEmail(false)
    onClose()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedTrainerId) {
      showToast('Please select a trainer', 'error')
      return
    }
    linkMutation.mutate({
      trainerId: selectedTrainerId,
      contractId: contract.id,
      sendEmail
    })
  }

  if (!isOpen) return null

  const trainers = trainersData?.data || []
  const linkedTrainerIds = contract.trainers?.map(t => t.id) || []
  const availableTrainers = trainers.filter(t => !linkedTrainerIds.includes(t.id))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Link Trainer to Contract</h2>
            <p className="text-sm text-gray-500 mt-1">
              {contract.title || contract.uid}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Trainers
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Trainer List */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Trainers
              </label>
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading trainers...</div>
              ) : availableTrainers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery ? 'No trainers found matching your search' : 'All trainers are already linked'}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-80 overflow-y-auto">
                  {availableTrainers.map((trainer) => (
                    <label
                      key={trainer.id}
                      className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                        selectedTrainerId === trainer.id
                          ? 'bg-primary-50 hover:bg-primary-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="trainer"
                        value={trainer.id}
                        checked={selectedTrainerId === trainer.id}
                        onChange={() => setSelectedTrainerId(trainer.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {trainer.first_name} {trainer.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{trainer.email}</p>
                        {trainer.uid && (
                          <p className="text-xs text-gray-400">{trainer.uid}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Send Email Option */}
            {selectedTrainerId && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 rounded"
                />
                <label htmlFor="sendEmail" className="text-sm text-gray-700 cursor-pointer">
                  Send signing link email to trainer
                </label>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={linkMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={linkMutation.isPending}
              disabled={!selectedTrainerId}
            >
              <UserPlusIcon className="h-4 w-4 mr-1" />
              Link Trainer
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}